/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class CanvasAPI
 * @hideconstructor
 * @classdesc API class that is used to control a dashboard canvas behaviour.
 */
define([], () => {
	class CanvasAPI {

		/**
		 * @function CanvasAPI#getLayout
		 * @description Get the layout API
		 * @return {@link LayoutAPI Layout API}
		 *
		 * @deprecated use {@link CanvasAPI#getContent} instead
		 */
		getLayout() {}

		/**
		 * @function CanvasAPI#removeWidget
		 * @description Removes a widget from the canvas
		 * @param {string} unique identifier of widget to be removed
		 * @param {string} [sender] sender identifier
		 *
		 * @deprecated use {@link CanvasAPI#removeContent} instead
		 */
		removeWidget() {}

		/**
		 * @function CanvasAPI#getWidgetWhenReady
		 * @description Wait for the widget with the given id is loaded and rendered.<br>
		 * If the widget is already loaded, an alternative synchronous way to get a widget is {@link CanvasAPI#getWidget getWidget}
		 * @param {String} id unique widget identifier
		 * @return {Promise<WidgetAPI>}
		 *
		 * @deprecated use {@link CanvasAPI#getContent} instead
		 */
		getWidgetWhenReady() {}

		/**
		 * @function CanvasAPI#getWidgetsWhenRenderComplete
		 * @description Wait for the given widgets to be rendered<br>
		 * Replaces {@link DashboardAPI#waitTillWidgetsRendered DashboardAPI.waitTillWidgetsRendered}
		 *
		 * @deprecated use {@link CanvasAPI#getContent} instead
		 */
		getWidgetsWhenRendered() {}

		/**
		 * @function CanvasAPI#selectWidget
		 * @description Select a widget
		 * @param {string} widgetId unique widget identifier
		 * @param {object} options selection options
		 * @param {boolean} [options.isTouch] Indicate if it is a touch gesture so that we can render touch friendly handles
		 * @return {Promise<WidgetAPI>}
		 *
		 * @deprecated use {@link CanvasAPI#selectContent} instead
		 */
		selectWidget() {}

		/**
		 * @function CanvasAPI#selectWidgets
		 * @description Select multiple widgets
		 * @param {string[]} widgetId array of unique widget identifiers
		 * @param {object} options selection options
		 * @param {boolean} [options.isTouch] Indicate if it is a touch gesture so that we can render touch friendly handles
		 * @return {Promise<WidgetAPI[]>}
		 *
		 * @deprecated use {@link CanvasAPI#selectContent} instead
		 */
		selectWidgets() {}

		/**
		 * @function CanvasAPI#getSelectedWidgets
		 * @description Get an array of currently selected widget APIs
		 * @return {Promise<WidgetAPI[]>}
		 *
		 * @deprecated use {@link CanvasAPI#getSelectedContentList} instead
		 */
		getSelectedWidgets() {}

		/**
		 * @function CanvasAPI#deselectAllWidgets
		 * @description Deselect all widgets that are currently selected
		 *
		 * @deprecated use {@link CanvasAPI#deselectContent} instead
		 */
		deselectAllWidgets() {}

		/**
		 * @function CanvasAPI#getWidget
		 * @description Return a widget Api object. If the widget hasn't loaded yet, this will return null
		 * An alternative way to get a widget that is not loaded yet is {@link CanvasAPI#getWidgetWhenReady getWidgetWhenReady}
		 * @param {string} id unique widget identifier
		 * @return {WidgetAPI} Widget Api object
		 *
		 * @deprecated use {@link CanvasAPI#getContent} instead
		 */
		getWidget() {}

		/**
		 * @function CanvasAPI#getWidgets
		 * @description Get an array of all loaded widget APIs
		 * @return {WidgetAPI[]} - Array of widget API objects
		 *
		 * @deprecated use {@link CanvasAPI#findContent} instead
		 */
		getWidgets() {}

		/**
		 * @function CanvasAPI#hasMaximizedWidget
		 * @description Indicate whether the canvas contains a maximized widget
		 * @return {boolean} true if the canvas contains a maximized widget, otherwise false
		 *
		 * @deprecated
		 */
		hasMaximizedWidget() {}


		/**
		 * @function CanvasAPI#getFeature
		 * @deprecated Replaced by {@link DashboardAPI#getFeature DashboardAPI.getFeature}
		 */
		getFeature() {}

		/**
		 * @deprecated
		 */
		registerFeature() {}

		/**
		 * @deprecated
		 * @function CanvasAPI#addFragment
		 * @description Add a fragment containing widgets and layout spec
		 * @param {object} fragment containing widgets and layout spec
		 *
		 * @deprecated Use {@link CanvasAPI#addContent}
		 */
		addFragment() {}

		/**
		 * @deprecated Use Filters feature
		 */
		getFilters() {}
	}

	return CanvasAPI;
});
